import {
  DEFAULT_ACTIVE_SHOP_LOCALES,
  FALLBACK_LOCALE_BY_LANGUAGE,
  LOCALE_ALIASES,
  LOCALE_CODE_REGEX,
  type LocaleResolutionOptions,
  type ShopLanguage,
  type ShopLocale,
} from './shared'

const canonicalizeLocale = (value: string): ShopLocale | null => {
  const trimmed = value.trim()
  if (!trimmed || !LOCALE_CODE_REGEX.test(trimmed)) return null

  const parts = trimmed.split('-').filter(Boolean)
  if (!parts.length) return null
  if (parts.length < 2) return null

  const [language, ...rest] = parts
  const normalized = [
    language.toLowerCase(),
    ...rest.map((segment) => {
      if (segment.length === 2) return segment.toUpperCase()
      return `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`
    }),
  ]

  return normalized.join('-')
}

export const localeToPathSegment = (locale: ShopLocale) => {
  const canonical = canonicalizeLocale(locale)
  if (!canonical) return 'it'
  return canonical.split('-')[0]?.toLowerCase() || 'it'
}

const buildLocaleFromLanguageSegment = (segment: string): ShopLocale => {
  const language = segment.toLowerCase()
  if (language === 'it' || language === 'en') {
    return FALLBACK_LOCALE_BY_LANGUAGE[language]
  }
  return `${language}-${language.toUpperCase()}`
}

const dedupeLocales = (locales: ShopLocale[]) => {
  const seen = new Set<string>()
  const result: ShopLocale[] = []

  for (const locale of locales) {
    const canonical = canonicalizeLocale(locale)
    if (!canonical) continue
    const key = canonical.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(canonical)
  }

  return result
}

export const normalizeActiveShopLocales = (locales: unknown): ShopLocale[] => {
  if (!Array.isArray(locales)) return [...DEFAULT_ACTIVE_SHOP_LOCALES]
  const normalized = dedupeLocales(locales.filter((value): value is string => typeof value === 'string'))
  if (!normalized.length) return [...DEFAULT_ACTIVE_SHOP_LOCALES]
  return normalized
}

const resolveAvailableLocales = (options?: LocaleResolutionOptions) =>
  normalizeActiveShopLocales(options?.availableLocales)

const matchLocaleAgainstAvailable = (
  candidate: ShopLocale,
  availableLocales: ShopLocale[],
  allowUnavailable: boolean,
): ShopLocale | null => {
  const candidateKey = candidate.toLowerCase()
  const exact = availableLocales.find((locale) => locale.toLowerCase() === candidateKey)
  if (exact) return exact

  const candidateLanguage = localeToPathSegment(candidate)
  const sameLanguage = availableLocales.find((locale) => localeToPathSegment(locale) === candidateLanguage)
  return sameLanguage ?? (allowUnavailable ? candidate : null)
}

export const normalizeShopLocale = (
  value: unknown,
  options?: LocaleResolutionOptions,
): ShopLocale | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const normalizedLower = trimmed.toLowerCase()
  const availableLocales = resolveAvailableLocales(options)
  const restrictToAvailable = Array.isArray(options?.availableLocales)

  const alias = LOCALE_ALIASES[normalizedLower]
  if (alias) {
    return matchLocaleAgainstAvailable(alias, availableLocales, !restrictToAvailable)
  }

  const canonical = canonicalizeLocale(trimmed)
  if (canonical) {
    return matchLocaleAgainstAvailable(canonical, availableLocales, !restrictToAvailable)
  }

  if (/^[a-z]{2,3}$/i.test(trimmed) && Array.isArray(options?.availableLocales)) {
    return matchLocaleAgainstAvailable(buildLocaleFromLanguageSegment(trimmed), availableLocales, false)
  }

  return null
}

export const localeToLanguage = (locale: ShopLocale): ShopLanguage =>
  localeToPathSegment(locale) === 'en' ? 'en' : 'it'

export const languageToLocale = (
  language: ShopLanguage,
  availableLocales?: ShopLocale[],
): ShopLocale => {
  const resolvedAvailableLocales = normalizeActiveShopLocales(availableLocales)
  const fromAvailable = resolvedAvailableLocales.find(
    (locale) => localeToPathSegment(locale) === language,
  )
  return fromAvailable ?? FALLBACK_LOCALE_BY_LANGUAGE[language]
}
